import {
  Box,
  Modal,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { useMyContext } from "../../context";
import { modalStyle } from "../../MUIStyles";
import DialogsButtons from "../DialogsButtons";
import AddEditRole from "./AddEditRole";
import useRoles from "../../hooks/useRoles";
import useEmployees from "../../hooks/useEmployees";
import { useState, useEffect, useRef } from "react";
import AuthenticatedImage from "../AuthenticatedImage";

const AddEditEmployee = () => {
  const {
    modalStates,
    handleCloseModal,
    handleOpenModal,
    setEmployeeData,
    employeeData,
    mode,
    setMode,
    setId,
  } = useMyContext();
  const {
    addupdateEmployeeLoading,
    handleAddUpdateEmployee,
    employeeById,
    employeeByIdLoading,
    employeeByIdError,
    employeeByIdIsError,
  } = useEmployees();

  const { data } = useRoles();
  const fileInputRef = useRef(null);

  // Local state for photo preview
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const isEditMode = mode === "update";

  const resetAddEmployeeForm = () => {
    setEmployeeData({
      name: "",
      phone_number: "",
      password: "",
      role_id: 0,
      photo: undefined,
      active: 1,
    });
    setPhotoPreview(null);
    setSelectedFile(null);
  };

  // Pre-fill form data when in edit mode
  useEffect(() => {
    if (isEditMode && employeeById?.data) {
      const employeeData = employeeById.data;
      const existingPhoto = employeeData?.photo_url;

      setEmployeeData({
        name: employeeData?.name || "",
        phone_number: employeeData?.phone_number || "",
        password: "", // Keep password empty in edit mode
        role_id: employeeData?.role?.id || 0,
        photo: undefined,
        active: employeeData?.active ? 1 : 0,
      });

      // Set photo preview to existing photo
      setPhotoPreview(existingPhoto || null);
      setSelectedFile(null);
    } else if (!isEditMode && modalStates.addEditEmployee) {
      // Always reset when opening in add mode (prevents stale cached values)
      resetAddEmployeeForm();
    }
  }, [
    isEditMode,
    employeeById,
    setEmployeeData,
    modalStates.addEditEmployee,
  ]);

  const closeModal = () => {
    setPhotoPreview(null);
    setSelectedFile(null);
    handleCloseModal("addEditEmployee");
    resetAddEmployeeForm();
    setId("");
    setMode("add");
  };

  // State for showing/hiding password
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);

      // Create object URL for preview
      const objectUrl = URL.createObjectURL(file);
      setPhotoPreview(objectUrl);

      // Update employeeData with the selected file
      setEmployeeData((prev) => ({
        ...prev,
        photo: file,
      }));
    }
  };

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      if (photoPreview && photoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  // Handle click on the image preview to change photo
  const handleImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setEmployeeData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseInt(value, 10) || 0 : value,
    }));
  };

  // Handle select change for role
  const handleRoleChange = (e) => {
    const roleId = parseInt(e.target.value, 10) || 0;
    setEmployeeData((prev) => ({
      ...prev,
      role_id: roleId,
    }));
  };

  // Handle checkbox change for active status
  const handleActiveChange = (e) => {
    const isActive = e.target.checked ? 1 : 0;
    setEmployeeData((prev) => ({
      ...prev,
      active: isActive,
    }));
  };

  // Render photo preview
  const renderPhotoPreview = () => {
    const employeeName = employeeById?.data?.name || employeeData.name;

    if (photoPreview) {
      // Check if it's a blob URL (new upload) or existing URL
      const isBlobUrl = photoPreview.startsWith("blob:");

      if (isBlobUrl) {
        // For newly uploaded photo
        return (
          <div
            onClick={handleImageClick}
            className="w-[130px] h-[130px] rounded-xl border-2 border-dashed border-fifth cursor-pointer overflow-hidden relative group"
          >
            <img
              src={photoPreview}
              alt="Preview"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-xs">Change Photo</span>
            </div>
          </div>
        );
      } else {
        // For existing photo from API
        return (
          <div
            onClick={handleImageClick}
            className="w-[130px] h-[130px] rounded-xl border-2 border-dashed border-fifth cursor-pointer overflow-hidden relative group"
          >
            <AuthenticatedImage
              src={photoPreview}
              alt={employeeName || "Profile"}
              className="w-full h-full object-cover"
              fallback={
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-500 text-sm">
                    {employeeName?.charAt(0)?.toUpperCase() || "?"}
                  </span>
                </div>
              }
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-xs">Change Photo</span>
            </div>
          </div>
        );
      }
    } else {
      // No photo - show upload placeholder
      return (
        <label
          htmlFor="photo"
          className="w-[130px] h-[130px] rounded-xl border-2 border-dashed border-fifth flex flex-col items-center justify-center text-xs text-fifth gap-3 cursor-pointer"
        >
          <img src="/img/upload.svg" alt="Upload icon" />
          {isEditMode ? "Change Photo (Optional)" : "Select Photo"}
        </label>
      );
    }
  };

  // Loading State for employee details (only in edit mode)
  if (modalStates.addEditEmployee && isEditMode && employeeByIdLoading) {
    return (
      <Modal open={modalStates.addEditEmployee} onClose={closeModal}>
        <Box
          sx={{
            ...modalStyle,
            maxHeight: "90vh",
            width: { lg: "600px", xs: "400px" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div className="flex flex-col items-center gap-4">
            <CircularProgress />
            <p className="text-gray-600">Loading employee details...</p>
          </div>
        </Box>
      </Modal>
    );
  }

  // Error State for employee details (only in edit mode)
  if (modalStates.addEditEmployee && isEditMode && employeeByIdIsError) {
    return (
      <Modal open={modalStates.addEditEmployee} onClose={closeModal}>
        <Box
          sx={{
            ...modalStyle,
            maxHeight: "90vh",
            width: { lg: "600px", xs: "400px" },
          }}
        >
          <div className="flex flex-col gap-6 items-center p-6">
            <div className="text-red-500 text-center">
              Failed to load employee details:{" "}
              {employeeByIdError?.message || "Unknown error occurred"}
            </div>
            <DialogsButtons
              color="secondary"
              Btn1="Close"
              Btn2="Retry"
              onClose={closeModal}
              onConfirm={() => window.location.reload()}
            />
          </div>
        </Box>
      </Modal>
    );
  }

  // Main Component
  return (
    <>
      <Modal open={modalStates.addEditEmployee} onClose={closeModal}>
        <Box
          sx={{
            ...modalStyle,
            maxHeight: "90vh",
            width: { lg: "600px", xs: "400px" },
          }}
        >
          <div>
            <div className="flex flex-col gap-8 items-center font-semibold">
              <h3 className="text-xl">
                {isEditMode ? "Edit Employee" : "Add New Employee"}
              </h3>

              {isEditMode && !employeeById?.data && !employeeByIdLoading && (
                <div className="text-red-500 text-sm">
                  Failed to load employee data. Please try again.
                </div>
              )}

              <div className="w-full flex flex-col items-start gap-5">
                {/* Profile photo section */}
                <div className="flex flex-col gap-4">
                  <span>Profile Photo:</span>
                  <div className="relative">
                    {renderPhotoPreview()}

                    <input
                      ref={fileInputRef}
                      type="file"
                      id="photo"
                      name="photo"
                      className="hidden"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </div>
                  {isEditMode && (
                    <p className="text-xs text-gray-500">
                      Select a new photo to change, or leave as is
                    </p>
                  )}
                </div>

                <div className="w-full flex flex-col gap-3">
                  <label htmlFor="name">Full Name:</label>
                  <input
                    type="text"
                    name="name"
                    className="input-style"
                    placeholder="Enter full name"
                    value={employeeData.name}
                    onChange={handleInputChange}
                    required
                    disabled={employeeByIdLoading}
                  />
                </div>

                <div className="w-full flex flex-col gap-3">
                  <label htmlFor="phone_number">Phone Number:</label>
                  <input
                    type="text"
                    name="phone_number"
                    className="input-style"
                    placeholder="Enter phone number"
                    value={employeeData.phone_number}
                    onChange={handleInputChange}
                    required
                    disabled={employeeByIdLoading}
                  />
                </div>

                <div className="w-full flex flex-col gap-3">
                  <label htmlFor="password">Password:</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="input-style pr-10 w-full"
                      placeholder={
                        isEditMode
                          ? "Enter new password (optional)"
                          : "Enter password"
                      }
                      value={employeeData.password}
                      onChange={handleInputChange}
                      required={!isEditMode}
                      disabled={employeeByIdLoading}
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={employeeByIdLoading}
                    >
                      {showPassword ? (
                        <img
                          src="/img/opened-eye.png"
                          alt="Hide password"
                          className="w-5 h-5"
                        />
                      ) : (
                        <img
                          src="/img/eye-closed.svg"
                          alt="Show password"
                          className="w-5 h-5"
                        />
                      )}
                    </button>
                  </div>
                  {isEditMode && (
                    <p className="text-xs text-gray-500">
                      Leave empty to keep existing password
                    </p>
                  )}
                </div>

                <div className="w-full font-bold flex flex-col gap-3">
                  <div className="font-semibold flex items-center justify-between">
                    <label className="block font-medium text-lg">Role:</label>
                    <button
                      type="button"
                      onClick={() => handleOpenModal("addEditRole")}
                      className="w-7 h-7 rounded-sm bg-secondary flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={employeeByIdLoading}
                    >
                      <img src="/img/add.svg" alt="Add role" />
                    </button>
                  </div>
                  <select
                    name="role_id"
                    className="w-full p-3 bg-fifth rounded-xl text-bg text-xs outline-none h-[50px] disabled:opacity-50 disabled:cursor-not-allowed"
                    value={employeeData.role_id}
                    onChange={handleRoleChange}
                    required
                    disabled={employeeByIdLoading}
                  >
                    <option value="">Select role</option>
                    {data?.data?.list?.map((el) => (
                      <option key={el?.id} value={el?.id}>
                        {el?.name}
                      </option>
                    )) || []}
                  </select>
                </div>

                {isEditMode && (
                  <div className="w-full flex flex-col gap-3">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={employeeData.active === 1}
                          onChange={handleActiveChange}
                          name="active"
                          color="primary"
                          disabled={employeeByIdLoading}
                        />
                      }
                      label="Active"
                      disabled={employeeByIdLoading}
                    />
                  </div>
                )}

                {!isEditMode && (
                  <input
                    type="hidden"
                    name="active"
                    value={employeeData.active}
                    onChange={handleInputChange}
                  />
                )}
              </div>

              <DialogsButtons
                Btn1="Cancel"
                Btn2={
                  addupdateEmployeeLoading
                    ? isEditMode
                      ? "Updating..."
                      : "Adding..."
                    : isEditMode
                      ? "Update Employee"
                      : "Add Employee"
                }
                color="secondary"
                onClose={closeModal}
                onClick={handleAddUpdateEmployee}
                disable={addupdateEmployeeLoading || employeeByIdLoading}
              />
            </div>
          </div>
        </Box>
      </Modal>
      <AddEditRole />
    </>
  );
};

export default AddEditEmployee;
